import React from 'react'
import { Route, useHistory } from 'react-router-dom'
import { useEffectOnce } from 'react-use'
import { Toast, useToastState } from 'transfix-ui/components/Toast'
import { Box, Typography } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { BookableShipmentSavedSearchAlertPlaceholder } from 'apps/fleet-planner/components/bookable-shipment-saved-search/alert/BookableShipmentSavedSearchAlertPlaceholder'
import { DedicatedLanesContractRow } from 'apps/fleet-planner/components/dedicated-lanes/DedicatedLanesContractRow'
import { DedicatedLanesContractRowMobile } from 'apps/fleet-planner/components/dedicated-lanes/DedicatedLanesContractRowMobile'
import { DedicatedLanesRowLoadingState } from 'apps/fleet-planner/components/dedicated-lanes/DedicatedLanesRowLoadingState'
import { useDedicatedLanesApi } from 'apps/fleet-planner/hooks/useDedicatedLanesApi'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks/useFMSAnalytics/useFMSAnalytics'
import { Container } from 'components/layout'
import { DedicatedLanesContractModal } from './DedicatedLanesContractModal'

export const DedicatedLanesContractsPage = () => {
  const { isMobile } = useBreakpointContext()
  const { dedicatedLanes, isLoading, setDedicatedLanes } = useDedicatedLanesApi()
  const history = useHistory()
  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()

  const { trackEvent } = useFMSAnalytics()
  const dedicatedLaneCount = dedicatedLanes.length || 0
  useEffectOnce(() => {
    trackEvent('Dedicated Lanes Page', 'VIEWED_DEDICATED_LANES_PAGE')
  })

  const updateDedicatedLanes = (laneId: number, smsEnabled: boolean, newLaneId: string) => {
    const newLanes = dedicatedLanes.map(item => {
      if (item.id === laneId) {
        return {
          ...item,
          smsNotificationsEnabled: smsEnabled,
          id: newLaneId,
        }
      }

      return item
    })

    setDedicatedLanes(newLanes)
    history.push(`/dedicated-lanes/lane/${newLaneId}`)

    openToast({
      toastType: 'default',
      toastMessage: smsEnabled
        ? 'You subscribed to text message updates for this lane.'
        : 'You unsubscribed to text message updates for this lane.',
    })
  }

  return (
    <div>
      <Container hidePadding>
        {!isMobile && (
          <Box pt={2} px={2}>
            <Box pl={4}>
              <Typography variant='h3'>
                {`You have ${dedicatedLaneCount} dedicated lanes you're receiving loads for`}
              </Typography>
              {isLoading && (
                <Box pt={3}>
                  <DedicatedLanesRowLoadingState rows={8} height={110} />
                </Box>
              )}
              {!isLoading && dedicatedLaneCount === 0 && (
                <Box pt={3} pr={2}>
                  <BookableShipmentSavedSearchAlertPlaceholder
                    isEmailReminderVisible={false}
                    message='Your dedicated lanes'
                  />
                </Box>
              )}
              {!isLoading &&
                dedicatedLanes.map(dedicatedLane => {
                  return (
                    <Box key={dedicatedLane?.id} pt={3} pr={2}>
                      <DedicatedLanesContractRow dedicatedLane={dedicatedLane} />
                    </Box>
                  )
                })}
            </Box>
          </Box>
        )}
        {isLoading && isMobile && (
          <Box pt={3} p={1.5}>
            <DedicatedLanesRowLoadingState rows={5} height={150} />
          </Box>
        )}
        {!isLoading && dedicatedLaneCount === 0 && isMobile && (
          <Box pt={3} p={1.5}>
            <BookableShipmentSavedSearchAlertPlaceholder
              isEmailReminderVisible={false}
              message='Your dedicated lanes'
            />
          </Box>
        )}
        {!isLoading && isMobile && (
          <Box pb={2}>
            {dedicatedLanes.map(dedicatedLane => {
              return (
                <Box key={dedicatedLane?.id} pt={3} px={2}>
                  <DedicatedLanesContractRowMobile dedicatedLane={dedicatedLane} />
                </Box>
              )
            })}
          </Box>
        )}
      </Container>
      <Route exact path='/dedicated-lanes/contracts/:id'>
        <DedicatedLanesContractModal
          updateDedicatedLanes={updateDedicatedLanes}
          dedicatedLanes={dedicatedLanes}
          isOpen
        />
      </Route>
      <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
    </div>
  )
}
